import * as React from "react";
import { CircleDashed } from "lucide-react";

import { cn } from "../../lib/utils";

/** Header-bar breadcrumb; its last item is the page title in the Linear layout. */

export interface BreadcrumbItem {
  label: string;
  href?: string;
  /**
   * The crumb's mark. Every crumb carries one — a section glyph, or an entity
   * mark like a project's coloured initial — so the trail reads as a row of
   * recognisable places rather than a run of same-looking words. Omit it only
   * when there is genuinely nothing to say: `fallbackIcon` then stands in, so
   * the icon column never goes ragged.
   */
  icon?: React.ReactNode;
}

export interface BreadcrumbProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "children"
> {
  items: BreadcrumbItem[];
  /** Collapse the middle with "…" beyond this many items. Default 3. */
  maxItems?: number;
  /**
   * Stands in for an item with no `icon` of its own, so every crumb leads with
   * a mark. Pass `null` to opt a trail out entirely.
   */
  fallbackIcon?: React.ReactNode;
  /**
   * Render a crumb link. Supply a router `<Link>` here to keep SPA navigation;
   * defaults to a plain `<a href>`.
   */
  renderLink?: (
    item: BreadcrumbItem,
    props: { className: string; children: React.ReactNode },
  ) => React.ReactNode;
}

const crumbLinkClassName = cn(
  "truncate rounded-xs text-text-tertiary",
  "transition-[color] duration-100 ease-out-fast hover:text-text",
  "focus-visible:outline-none focus-bar",
);

/**
 * One 16px box for every mark, whatever it is. Phosphor and Lucide glyphs ship
 * at their own sizes and an entity mark (`ProjectGlyph`) is a filled tile, not
 * an svg — left alone they set the trail's baseline jumping crumb to crumb.
 * The `>*` rule outranks a child's own `size-*` class, so a mark sized for a
 * list row still lands at 16px here.
 */
const markClassName = cn(
  "flex size-4 shrink-0 items-center justify-center",
  "[&_svg]:size-4 [&>*]:size-4",
);

/** Keeps the first item and the tail, dropping the middle behind an ellipsis. */
function collapse(
  items: BreadcrumbItem[],
  maxItems: number,
): (BreadcrumbItem | "ellipsis")[] {
  if (maxItems < 2 || items.length <= maxItems) return items;
  const tail = items.slice(items.length - (maxItems - 1));
  return [items[0], "ellipsis", ...tail];
}

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  (
    {
      items,
      maxItems = 3,
      fallbackIcon = <CircleDashed aria-hidden="true" />,
      renderLink,
      className,
      ...props
    },
    ref,
  ) => {
    const visible = collapse(items, maxItems);

    return (
      <nav
        ref={ref}
        data-slot="breadcrumb"
        aria-label="Breadcrumb"
        className={cn("flex min-w-0 items-center", className)}
        {...props}
      >
        <ol className="flex min-w-0 items-center gap-1.5 text-sm">
          {visible.map((entry, index) => {
            const isLast = index === visible.length - 1;

            if (entry === "ellipsis") {
              return (
                <li
                  key="ellipsis"
                  className="flex shrink-0 items-center gap-1.5"
                  aria-hidden="true"
                >
                  <span className="text-text-disabled">/</span>
                  <span className="text-text-tertiary">…</span>
                </li>
              );
            }

            const mark = entry.icon ?? fallbackIcon;
            const content = (
              <>
                {mark ? (
                  <span aria-hidden="true" className={markClassName}>
                    {mark}
                  </span>
                ) : null}
                <span className="truncate">{entry.label}</span>
              </>
            );

            return (
              <li
                key={`${entry.label}-${index}`}
                className="flex min-w-0 items-center gap-1.5"
              >
                {index > 0 ? (
                  <span
                    aria-hidden="true"
                    className="shrink-0 text-text-disabled"
                  >
                    /
                  </span>
                ) : null}
                {isLast || !entry.href ? (
                  <span
                    aria-current={isLast ? "page" : undefined}
                    className={cn(
                      "flex min-w-0 items-center gap-1.5 truncate",
                      isLast ? "font-semibold text-text" : "text-text-tertiary",
                    )}
                  >
                    {content}
                  </span>
                ) : renderLink ? (
                  renderLink(entry, {
                    className: cn(
                      "flex min-w-0 items-center gap-1.5",
                      crumbLinkClassName,
                    ),
                    children: content,
                  })
                ) : (
                  <a
                    href={entry.href}
                    className={cn(
                      "flex min-w-0 items-center gap-1.5",
                      crumbLinkClassName,
                    )}
                  >
                    {content}
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  },
);
Breadcrumb.displayName = "Breadcrumb";

export { Breadcrumb };
